import React, { FC, useMemo, useState } from 'react';
import { formatDate } from 'utils/format';
import { Table, TableBody, TableHead, TableRow, TableCell, Box, IconButton, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { ScheduledStep, ScheduleStatus } from 'models/production-planning';
import PlanOverviewDialog from 'components/shared/production-scheduling/plan-overview-dialog/plan-overview-dialog.component';

type CellOrdersDialogProps = {
    open: boolean;
    onClose: () => void;
    steps: ScheduledStep[];
    stepName: string;
    hour: string;
    onStepChanged?: () => void;
};

const CellOrdersDialog: FC<CellOrdersDialogProps> = ({ open, onClose, steps, stepName, hour, onStepChanged }) => {
    const { t: translate } = useTranslation(['production-dashboard', 'orders']);
    const [overviewOrderId, setOverviewOrderId] = useState<string | null>(null);

    const uniqueOrders = useMemo(() => {
        const seen = new Set<string>();
        return steps.filter((step) => {
            if (seen.has(step.orderId)) return false;
            seen.add(step.orderId);
            return true;
        });
    }, [steps]);

    return (
        <DHDialog open={open} onClose={onClose} maxWidth="lg" fullWidth title={`${hour}: ${stepName}`}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 28 }} />
                        <TableCell>{translate('production-dashboard:ORDER_ID')}</TableCell>
                        <TableCell>{translate('production-dashboard:CUSTOMER')}</TableCell>
                        <TableCell>{translate('production-dashboard:PATIENT')}</TableCell>
                        <TableCell>{translate('production-dashboard:START_DATE')}</TableCell>
                        <TableCell>{translate('production-dashboard:DEADLINE')}</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {uniqueOrders.map((step) => (
                        <TableRow
                            key={step.orderId}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => window.open(`/order-details/${step.orderId}/main-view`, '_blank')}
                        >
                            <TableCell>
                                <Box display="flex" alignItems="center">
                                    {step.scheduleStatus === ScheduleStatus.LATE && (
                                        <WarningAmberIcon fontSize="small" color="error" />
                                    )}
                                    {step.scheduleStatus === ScheduleStatus.EARLY && (
                                        <CheckCircleIcon fontSize="small" color="success" />
                                    )}
                                    {step.scheduleStatus === ScheduleStatus.PAUSED && (
                                        <Tooltip title={translate('production-dashboard:STATUS_PAUSED')}>
                                            <PauseCircleIcon fontSize="small" color="warning" />
                                        </Tooltip>
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell>{step.orderDisplayId || step.orderId}</TableCell>
                            <TableCell>
                                {step.deliveryClinicName || step.clinicName || ''}
                                {step.dentistName ? ` (${step.dentistName})` : ''}
                            </TableCell>
                            <TableCell>{step.patientName || ''}</TableCell>
                            <TableCell>
                                {step.startDate ? formatDate(new Date(step.startDate), 'datetime') : '-'}
                            </TableCell>
                            <TableCell>{formatDate(new Date(step.deadline), 'datetime')}</TableCell>
                            <TableCell>
                                <Tooltip title={translate('orders:SHOW_PLAN_OVERVIEW')}>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOverviewOrderId(step.orderId);
                                        }}
                                    >
                                        <TimelineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <PlanOverviewDialog
                open={!!overviewOrderId}
                onClose={() => setOverviewOrderId(null)}
                orderId={overviewOrderId || undefined}
                withWorkflowActions
                onStepChanged={onStepChanged}
            />
        </DHDialog>
    );
};

export default CellOrdersDialog;
